import { CustomLoader } from "@admin/components/ui/custom-loader";
import { UseFormReturn } from "react-hook-form";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  INSTITUTION_TYPES,
  INSTITUTION_LEVELS,
  BOARDS,
  MEDIUM_OF_INSTRUCTION,
  BOARDING_GENDER,
  ESTABLISHED_YEARS,
} from "../../constants/institutionFormFields";
import {
  designationMasterAPI,
  districtsAPI,
  facilitiesMasterAPI,
  statesAPI,
  type DesignationMaster,
} from "../../services/api";

const MAX_INSTITUTION_IMAGES_DEFAULT = 15;

export interface InstitutionOnboardingFormProps {
  step: number;
  form: UseFormReturn<any>;
  onNext: (data: any) => void;
  onBack: () => void;
  onFinalSubmit: (data: any) => void | Promise<void>;
  selectedDepartments: string[];
  setSelectedDepartments: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFacilities: string[];
  setSelectedFacilities: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLevels: string[];
  setSelectedLevels: React.Dispatch<React.SetStateAction<string[]>>;
  institutionImages: File[];
  setInstitutionImages: React.Dispatch<React.SetStateAction<File[]>>;
  /** Existing image URLs from API (edit mode) — e.g. data.images array; user can remove */
  existingImageUrls?: string[];
  /** Called when user removes an existing image by index */
  onRemoveExistingImage?: (index: number) => void;
  brochureFile: File | null;
  setBrochureFile: React.Dispatch<React.SetStateAction<File | null>>;
  existingBrochureUrl?: string;
  onRemoveExistingBrochure?: () => void;
  isModal?: boolean;
  onCancel?: () => void;
  maxInstitutionImages?: number;
  isEditMode?: boolean;
}

type FacilityOption = {
  id: string;
  name: string;
};

const NUMBER_INPUT_CLASSNAME =
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

function toggleInArray(
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  value: string
) {
  setter(prev =>
    prev.includes(value) ? prev.filter(x => x !== value) : [...prev, value]
  );
}

export default function InstitutionOnboardingForm({
  step,
  form,
  onNext,
  onBack,
  onFinalSubmit,
  selectedFacilities,
  setSelectedFacilities,
  selectedLevels,
  setSelectedLevels,
  institutionImages,
  setInstitutionImages,
  existingImageUrls = [],
  onRemoveExistingImage,
  brochureFile,
  setBrochureFile,
  existingBrochureUrl,
  onRemoveExistingBrochure,
  isModal = false,
  onCancel,
  maxInstitutionImages = MAX_INSTITUTION_IMAGES_DEFAULT,
  isEditMode = false,
}: InstitutionOnboardingFormProps) {
  const [mainStates, setMainStates] = useState<{ id: string; name: string }[]>(
    []
  );
  const [mainDistricts, setMainDistricts] = useState<
    { id: string; name: string }[]
  >([]);
  const [mainCities, setMainCities] = useState<{ id: string; name: string }[]>(
    []
  );
  const [districtsStep2, setDistrictsStep2] = useState<
    { id: string; name: string }[]
  >([]);
  const [citiesStep2, setCitiesStep2] = useState<
    { id: string; name: string }[]
  >([]);
  const [loadingMainStates, setLoadingMainStates] = useState(false);
  const [loadingMainDistricts, setLoadingMainDistricts] = useState(false);
  const [loadingMainCities, setLoadingMainCities] = useState(false);
  const [loadingDistrictsStep2, setLoadingDistrictsStep2] = useState(false);
  const [loadingCitiesStep2, setLoadingCitiesStep2] = useState(false);
  const [facilityOptions, setFacilityOptions] = useState<FacilityOption[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [designations, setDesignations] = useState<DesignationMaster[]>([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [campusAreaValue, setCampusAreaValue] = useState("");
  const [campusAreaUnit, setCampusAreaUnit] = useState<"Sqft" | "Acre">("Sqft");

  const selectedMainStateId = String(form.watch("state_main") ?? "");
  const selectedMainDistrictId = String(form.watch("district_main") ?? "");
  const selectedStateIdStep2 = String(form.watch("state") ?? "");
  const selectedDistrictIdStep2 = String(form.watch("district") ?? "");
  const watchedDistrict = form.watch("district");
  const watchedCity = form.watch("city");
  const watchedDesignation = form.watch("designation");
  const watchedTrusteeDesignation = form.watch("trustee_designation");

  useEffect(() => {
    let mounted = true;
    const loadStates = async () => {
      setLoadingMainStates(true);
      try {
        const list = await statesAPI.list();
        if (!mounted) {
          return;
        }
        setMainStates(
          list.map(item => ({
            id: String(item.id),
            name: String(item.name ?? ""),
          }))
        );
      } catch {
        if (!mounted) {
          return;
        }
        setMainStates([]);
      } finally {
        if (mounted) {
          setLoadingMainStates(false);
        }
      }
    };
    loadStates();
    return () => {
      mounted = false;
    };
  }, []);

  const resolveStateId = useCallback(
    (idOrName: string) => {
      if (!idOrName) {
        return "";
      }
      if (/^\d+$/.test(idOrName)) {
        return idOrName;
      }
      const found = mainStates.find(
        s => s.name.toLowerCase() === idOrName.toLowerCase()
      );
      return found ? found.id : idOrName;
    },
    [mainStates]
  );

  useEffect(() => {
    let mounted = true;
    const loadMainDistricts = async () => {
      const stateId = resolveStateId(selectedMainStateId);
      if (!stateId || !/^\d+$/.test(stateId)) {
        setMainDistricts([]);
        return;
      }
      setLoadingMainDistricts(true);
      try {
        const list = await statesAPI.getDistrictsByStateId(stateId);
        if (!mounted) {
          return;
        }
        setMainDistricts(
          list.map(item => ({
            id: String(item.id),
            name: String(item.name ?? ""),
          }))
        );
      } catch {
        if (mounted) {
          setMainDistricts([]);
        }
      } finally {
        if (mounted) {
          setLoadingMainDistricts(false);
        }
      }
    };
    loadMainDistricts();
    return () => {
      mounted = false;
    };
  }, [selectedMainStateId, resolveStateId]);

  useEffect(() => {
    let mounted = true;
    const loadMainCities = async () => {
      if (!selectedMainDistrictId || !/^\d+$/.test(selectedMainDistrictId)) {
        setMainCities([]);
        return;
      }
      setLoadingMainCities(true);
      try {
        const list = await districtsAPI.getCitiesByDistrictId(
          selectedMainDistrictId
        );
        if (!mounted) {
          return;
        }
        setMainCities(
          list.map(item => ({
            id: String(item.id),
            name: String(item.name ?? ""),
          }))
        );
      } catch {
        if (mounted) {
          setMainCities([]);
        }
      } finally {
        if (mounted) {
          setLoadingMainCities(false);
        }
      }
    };
    loadMainCities();
    return () => {
      mounted = false;
    };
  }, [selectedMainDistrictId]);

  useEffect(() => {
    let mounted = true;
    const loadDistrictsStep2 = async () => {
      const stateId = resolveStateId(selectedStateIdStep2);
      if (!stateId || !/^\d+$/.test(stateId)) {
        setDistrictsStep2([]);
        return;
      }
      setLoadingDistrictsStep2(true);
      try {
        const list = await statesAPI.getDistrictsByStateId(stateId);
        if (!mounted) {
          return;
        }
        setDistrictsStep2(
          list.map(item => ({
            id: String(item.id),
            name: String(item.name ?? ""),
          }))
        );
      } catch {
        if (mounted) {
          setDistrictsStep2([]);
        }
      } finally {
        if (mounted) {
          setLoadingDistrictsStep2(false);
        }
      }
    };
    loadDistrictsStep2();
    return () => {
      mounted = false;
    };
  }, [selectedStateIdStep2, resolveStateId]);

  useEffect(() => {
    let mounted = true;
    const loadCitiesStep2 = async () => {
      if (!selectedDistrictIdStep2 || !/^\d+$/.test(selectedDistrictIdStep2)) {
        setCitiesStep2([]);
        return;
      }
      setLoadingCitiesStep2(true);
      try {
        const list = await districtsAPI.getCitiesByDistrictId(
          selectedDistrictIdStep2
        );
        if (!mounted) {
          return;
        }
        setCitiesStep2(
          list.map(item => ({
            id: String(item.id),
            name: String(item.name ?? ""),
          }))
        );
      } catch {
        if (mounted) {
          setCitiesStep2([]);
        }
      } finally {
        if (mounted) {
          setLoadingCitiesStep2(false);
        }
      }
    };
    loadCitiesStep2();
    return () => {
      mounted = false;
    };
  }, [selectedDistrictIdStep2]);

  // Normalize State/City names to IDs for Select components
  useEffect(() => {
    if (mainStates.length > 0) {
      const stateMain = form.getValues("state_main");
      if (stateMain && !/^\d+$/.test(stateMain)) {
        const found = mainStates.find(
          s => s.name.toLowerCase() === stateMain.toLowerCase()
        );
        if (found) {
          form.setValue("state_main", found.id);
        }
      }
      const state = form.getValues("state");
      if (state && !/^\d+$/.test(state)) {
        const found = mainStates.find(
          s => s.name.toLowerCase() === state.toLowerCase()
        );
        if (found) {
          form.setValue("state", found.id);
        }
      }
    }
  }, [mainStates, form]);

  useEffect(() => {
    if (mainDistricts.length > 0) {
      const districtMain = form.getValues("district_main");
      if (districtMain && !/^\d+$/.test(districtMain)) {
        const found = mainDistricts.find(
          d => d.name.toLowerCase() === districtMain.toLowerCase()
        );
        if (found) {
          form.setValue("district_main", found.id);
        }
      }
    }
  }, [mainDistricts, form]);

  useEffect(() => {
    if (mainCities.length > 0) {
      const cityMain = form.getValues("city_main");
      if (cityMain && !/^\d+$/.test(cityMain)) {
        const found = mainCities.find(
          c => c.name.toLowerCase() === cityMain.toLowerCase()
        );
        if (found) {
          form.setValue("city_main", found.id);
        }
      }
    }
  }, [mainCities, form]);

  useEffect(() => {
    if (districtsStep2.length > 0) {
      const district = form.getValues("district");
      if (district && !/^\d+$/.test(district)) {
        const found = districtsStep2.find(
          d => d.name.toLowerCase() === district.toLowerCase()
        );
        if (found) {
          form.setValue("district", found.id);
        }
      } else if (typeof district === "number") {
        form.setValue("district", String(district));
      }
    }
  }, [districtsStep2, form, watchedDistrict]);

  useEffect(() => {
    if (citiesStep2.length > 0) {
      const city = form.getValues("city");
      if (city && !/^\d+$/.test(city)) {
        const found = citiesStep2.find(
          c => c.name.toLowerCase() === city.toLowerCase()
        );
        if (found) {
          form.setValue("city", found.id);
        }
      } else if (typeof city === "number") {
        form.setValue("city", String(city));
      }
    }
  }, [citiesStep2, form, watchedCity]);

  useEffect(() => {
    if (designations.length > 0) {
      const currentDesignation = form.getValues("designation");
      if (currentDesignation) {
        if (!/^\d+$/.test(String(currentDesignation))) {
          const found = designations.find(
            d =>
              d.name.toLowerCase() === String(currentDesignation).toLowerCase()
          );
          if (found) {
            form.setValue("designation", String(found.id), {
              shouldValidate: true,
            });
          }
        } else if (typeof currentDesignation === "number") {
          form.setValue("designation", String(currentDesignation), {
            shouldValidate: true,
          });
        }
      }
    }
  }, [designations, form, watchedDesignation]);

  useEffect(() => {
    if (designations.length > 0) {
      const currentTrusteeDesignation = form.getValues("trustee_designation");
      if (currentTrusteeDesignation) {
        if (!/^\d+$/.test(String(currentTrusteeDesignation))) {
          const found = designations.find(
            d =>
              d.name.toLowerCase() ===
              String(currentTrusteeDesignation).toLowerCase()
          );
          if (found) {
            form.setValue("trustee_designation", String(found.id), {
              shouldValidate: true,
            });
          }
        } else if (typeof currentTrusteeDesignation === "number") {
          form.setValue(
            "trustee_designation",
            String(currentTrusteeDesignation),
            { shouldValidate: true }
          );
        }
      }
    }
  }, [designations, form, watchedTrusteeDesignation]);

  useEffect(() => {
    const combined = campusAreaValue.trim()
      ? `${campusAreaValue.trim()} ${campusAreaUnit}`
      : "";
    form.setValue("campus_area", combined);
  }, [campusAreaValue, campusAreaUnit, form]);

  useEffect(() => {
    let mounted = true;
    const loadDesignations = async () => {
      setLoadingDesignations(true);
      try {
        const list = await designationMasterAPI.list();
        if (!mounted) {
          return;
        }
        const activeDesignations = list
          .filter(d => d.is_active !== false)
          .sort((a, b) => (a.order_by ?? 0) - (b.order_by ?? 0));
        setDesignations(activeDesignations);

        // Normalization moved to separate useEffect hooks above
      } catch {
        if (mounted) {
          setDesignations([]);
        }
      } finally {
        if (mounted) {
          setLoadingDesignations(false);
        }
      }
    };
    loadDesignations();
    return () => {
      mounted = false;
    };
  }, [form]);

  useEffect(() => {
    let mounted = true;
    const loadFacilities = async () => {
      setLoadingFacilities(true);
      try {
        const list = await facilitiesMasterAPI.list();
        if (!mounted) {
          return;
        }

        const apiFacilities = list
          .filter(
            item => item.is_active !== false && String(item.name ?? "").trim()
          )
          .sort((a, b) => {
            const orderA = a.order_by ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order_by ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return String(a.name).localeCompare(String(b.name));
          })
          .map(item => ({
            id: String(item.id),
            name: String(item.name).trim(),
          }));

        const normalizedSelected = Array.from(
          new Set(
            selectedFacilities
              .map(facility => {
                const value = facility.trim();
                if (!value) {
                  return "";
                }
                const matchedById = apiFacilities.find(
                  item => item.id === value
                );
                if (matchedById) {
                  return matchedById.id;
                }
                const matchedByName = apiFacilities.find(
                  item => item.name === value
                );
                return matchedByName?.id ?? value;
              })
              .filter(Boolean)
          )
        );

        const selectedOnly = normalizedSelected
          .filter(
            facilityId => !apiFacilities.some(item => item.id === facilityId)
          )
          .map(facilityId => ({
            id: facilityId,
            name: facilityId,
          }));

        setFacilityOptions([...apiFacilities, ...selectedOnly]);
        if (
          JSON.stringify(normalizedSelected) !==
          JSON.stringify(selectedFacilities)
        ) {
          setSelectedFacilities(normalizedSelected);
        }
      } catch {
        if (!mounted) {
          return;
        }
        setFacilityOptions(
          selectedFacilities.filter(Boolean).map(facility => ({
            id: facility,
            name: facility,
          }))
        );
      } finally {
        if (mounted) {
          setLoadingFacilities(false);
        }
      }
    };

    loadFacilities();
    return () => {
      mounted = false;
    };
  }, [selectedFacilities, setSelectedFacilities]);

  const toggleFacility = (facilityId: string) =>
    toggleInArray(setSelectedFacilities, facilityId);
  const toggleLevel = (level: string) =>
    toggleInArray(setSelectedLevels, level);

  const [isFinalSubmitting, setIsFinalSubmitting] = useState(false);
  const finalSubmitLockRef = useRef(false);

  const handleFinalSubmitWrapped = async (data: any) => {
    if (finalSubmitLockRef.current) {
      return;
    }
    finalSubmitLockRef.current = true;
    setIsFinalSubmitting(true);
    try {
      await Promise.resolve(onFinalSubmit(data));
    } finally {
      finalSubmitLockRef.current = false;
      setIsFinalSubmitting(false);
    }
  };

  if (step === 1) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Basic Information</h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>
                Institution Name <span className="text-destructive">*</span>
              </Label>
              <Input
                {...form.register("name", {
                  required: "Institution Name is required",
                })}
                placeholder="Enter institution name"
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.name.message)}
                </p>
              )}
            </div>
            <div>
              <Label>
                Institution Type <span className="text-destructive">*</span>
              </Label>
              <input
                type="hidden"
                {...form.register("type", {
                  required: "Institution Type is required",
                })}
              />
              <RadioGroup
                value={String(form.watch("type") ?? "")}
                onValueChange={(value: string) =>
                  form.setValue("type", value, { shouldValidate: true })
                }
                className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2"
              >
                {INSTITUTION_TYPES.map(type => (
                  <label
                    key={type.value}
                    htmlFor={`institution-type-${type.value}`}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors hover:bg-muted",
                      String(form.watch("type") ?? "") === type.value
                        ? "border-primary bg-primary/10"
                        : ""
                    )}
                  >
                    <RadioGroupItem
                      id={`institution-type-${type.value}`}
                      value={type.value}
                    />
                    <span className="text-sm font-medium">{type.label}</span>
                  </label>
                ))}
              </RadioGroup>
              {form.formState.errors.type && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.type.message)}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>
                Year Established <span className="text-destructive">*</span>
              </Label>
              <input
                type="hidden"
                {...form.register("year_established", {
                  required: "Year Established is required",
                })}
              />
              <Select
                value={
                  String(form.watch("year_established") ?? "") || undefined
                }
                onValueChange={(value: string) =>
                  form.setValue("year_established", value, {
                    shouldValidate: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {ESTABLISHED_YEARS.map(year => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.year_established && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.year_established.message)}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>
                Contact Person Name <span className="text-destructive">*</span>
              </Label>
              <Input
                {...form.register("contact_person", {
                  required: "Contact Person Name is required",
                })}
                placeholder="Enter contact person name"
              />
              {form.formState.errors.contact_person && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.contact_person.message)}
                </p>
              )}
            </div>
            <div>
              <Label>Designation</Label>
              <input type="hidden" {...form.register("designation")} />
              <Select
                value={String(form.watch("designation") ?? "") || undefined}
                onValueChange={(value: string) =>
                  form.setValue("designation", value, { shouldValidate: true })
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loadingDesignations
                        ? "Loading designations..."
                        : "Select designation"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {designations.map(d => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                type="email"
                {...form.register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
                placeholder="Enter email address"
              />
              {form.formState.errors.email && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.email.message)}
                </p>
              )}
            </div>
            <div>
              <Label>
                Phone <span className="text-destructive">*</span>
              </Label>
              <Input
                {...form.register("phone", {
                  required: "Phone is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit phone number",
                  },
                })}
                placeholder="Enter phone number"
                maxLength={10}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {form.formState.errors.phone && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.phone.message)}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label>Website</Label>
            <Input
              {...form.register("website", {
                validate: value =>
                  !value ||
                  /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i.test(value) ||
                  "Enter a valid website URL",
              })}
              placeholder="https://"
            />
            {form.formState.errors.website && (
              <p className="mt-1 text-xs text-destructive">
                {String(form.formState.errors.website.message)}
              </p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>
                State <span className="text-destructive">*</span>
              </Label>
              <input
                type="hidden"
                {...form.register("state_main", {
                  required: "State is required",
                })}
              />
              <Select
                value={String(form.watch("state_main") ?? "") || undefined}
                onValueChange={(value: string) => {
                  form.setValue("state_main", value, { shouldValidate: true });
                  form.setValue("city_main", "", { shouldValidate: true });
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loadingMainStates ? "Loading states..." : "Select state"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {mainStates.map(state => (
                    <SelectItem key={state.id} value={state.id}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.state_main && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.state_main.message)}
                </p>
              )}
            </div>
            <div>
              <Label>
                District <span className="text-destructive">*</span>
              </Label>
              <input
                type="hidden"
                {...form.register("district_main", {
                  required: "District is required",
                })}
              />
              <Select
                value={String(form.watch("district_main") ?? "") || undefined}
                onValueChange={(value: string) => {
                  form.setValue("district_main", value, {
                    shouldValidate: true,
                  });
                  form.setValue("city_main", "", { shouldValidate: true });
                }}
                disabled={!selectedMainStateId || loadingMainDistricts}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedMainStateId
                        ? "Select state first"
                        : loadingMainDistricts
                          ? "Loading districts..."
                          : "Select district"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {mainDistricts.map(district => (
                    <SelectItem key={district.id} value={district.id}>
                      {district.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.district_main && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.district_main.message)}
                </p>
              )}
            </div>
            <div>
              <Label>
                City <span className="text-destructive">*</span>
              </Label>
              <input
                type="hidden"
                {...form.register("city_main", {
                  required: "City is required",
                })}
              />
              <Select
                value={String(form.watch("city_main") ?? "") || undefined}
                onValueChange={(value: string) => {
                  form.setValue("city_main", value, { shouldValidate: true });
                }}
                disabled={!selectedMainDistrictId || loadingMainCities}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedMainDistrictId
                        ? "Select district first"
                        : loadingMainCities
                          ? "Loading cities..."
                          : "Select city"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {mainCities.map(city => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.city_main && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.city_main.message)}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label>
              Images & Videos (multiple upload){" "}
              <span className="text-destructive">*</span>
            </Label>
            <input
              type="hidden"
              {...form.register("institution_media_required", {
                validate: () =>
                  institutionImages.length + existingImageUrls.length > 0 ||
                  "At least one image or video is required",
              })}
            />
            <p className="mb-2 text-xs text-muted-foreground">
              Upload up to {maxInstitutionImages} images or videos of your
              institution (building, campus, etc.)
            </p>
            <div
              className={cn(
                "relative flex min-h-[120px] flex-col items-center justify-center rounded-lg border-2 border-dashed bg-muted/30 p-4 transition-colors",
                institutionImages.length + (existingImageUrls?.length || 0) >=
                  maxInstitutionImages
                  ? "cursor-not-allowed border-muted-foreground/20 opacity-60"
                  : "border-muted-foreground/25 hover:border-muted-foreground/40 hover:bg-muted/50"
              )}
              onDragOver={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                if (
                  institutionImages.length + (existingImageUrls?.length || 0) >=
                  maxInstitutionImages
                ) {
                  return;
                }
                const files = Array.from(e.dataTransfer.files).filter(
                  f =>
                    f.type.startsWith("image/") || f.type.startsWith("video/")
                );
                if (files.length) {
                  const remainingSlots =
                    maxInstitutionImages -
                    (institutionImages.length +
                      (existingImageUrls?.length || 0));
                  setInstitutionImages(prev => [
                    ...prev,
                    ...files.slice(0, remainingSlots),
                  ]);
                }
              }}
            >
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                disabled={
                  institutionImages.length + (existingImageUrls?.length || 0) >=
                  maxInstitutionImages
                }
                className="absolute inset-0 z-10 cursor-pointer opacity-0 disabled:pointer-events-none"
                onChange={e => {
                  if (
                    institutionImages.length +
                      (existingImageUrls?.length || 0) >=
                    maxInstitutionImages
                  ) {
                    return;
                  }
                  const files = e.target.files
                    ? Array.from(e.target.files)
                    : [];
                  if (files.length) {
                    const remainingSlots =
                      maxInstitutionImages -
                      (institutionImages.length +
                        (existingImageUrls?.length || 0));
                    setInstitutionImages(prev => [
                      ...prev,
                      ...files.slice(0, remainingSlots),
                    ]);
                  }
                  e.target.value = "";
                }}
              />
              <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
              <span className="text-center text-sm text-muted-foreground">
                {institutionImages.length + (existingImageUrls?.length || 0) >=
                maxInstitutionImages
                  ? `Maximum ${maxInstitutionImages} items reached`
                  : "Drag & drop images/videos here or click to browse"}
              </span>
              <span className="mt-1 text-xs text-muted-foreground">
                Maximum {maxInstitutionImages} images/videos
              </span>
            </div>
            {(existingImageUrls.length > 0 || institutionImages.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-3">
                {existingImageUrls.map((url, index) => {
                  const isVideo =
                    url.toLowerCase().endsWith(".mp4") ||
                    url.toLowerCase().endsWith(".webm") ||
                    url.toLowerCase().endsWith(".ogg") ||
                    url.toLowerCase().endsWith(".mov") ||
                    url.toLowerCase().endsWith(".m4v");
                  return (
                    <div
                      key={url}
                      className="group relative flex flex-col items-center"
                    >
                      <div className="relative h-20 w-20 overflow-hidden rounded-lg border bg-muted">
                        {isVideo ? (
                          <video
                            src={url}
                            className="h-full w-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={url}
                            alt={`Institution ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        )}
                        {onRemoveExistingImage && (
                          <button
                            type="button"
                            onClick={() => onRemoveExistingImage(index)}
                            className="text-destructive-foreground absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/90 opacity-0 transition-opacity group-hover:opacity-100"
                            aria-label="Remove item"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <span className="mt-1 max-w-[100px] truncate text-xs text-muted-foreground">
                        {isVideo ? "Video" : "Image"} {index + 1}
                      </span>
                    </div>
                  );
                })}
                {institutionImages.map((file, index) => {
                  const isVideo = file.type.startsWith("video/");
                  return (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="group relative flex flex-col items-center"
                    >
                      <div className="relative h-20 w-20 overflow-hidden rounded-lg border bg-muted">
                        {isVideo ? (
                          <video
                            src={URL.createObjectURL(file)}
                            className="h-full w-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="h-full w-full object-cover"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setInstitutionImages(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-destructive-foreground absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/90 opacity-0 transition-opacity group-hover:opacity-100"
                          aria-label="Remove item"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="mt-1 max-w-[100px] truncate text-xs text-muted-foreground">
                        {file.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {form.formState.errors.institution_media_required && (
              <p className="mt-2 text-xs text-destructive">
                {String(
                  form.formState.errors.institution_media_required.message
                )}
              </p>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          {isModal && onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              size="lg"
            >
              Cancel
            </Button>
          )}
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 2) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Address Information</h3>

          <div>
            <Label>Address Line 1</Label>
            <Input
              {...form.register("address_line1")}
              placeholder="Building name, street address"
            />
          </div>

          <div>
            <Label>Address Line 2</Label>
            <Input
              {...form.register("address_line2")}
              placeholder="Area, landmark"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>State</Label>
              <input type="hidden" {...form.register("state")} />
              <Select
                value={String(form.watch("state") ?? "") || undefined}
                onValueChange={(value: string) => {
                  form.setValue("state", value);
                  form.setValue("district", "");
                  form.setValue("city", "");
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loadingMainStates ? "Loading states..." : "Select state"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {mainStates.map(state => (
                    <SelectItem key={state.id} value={state.id}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>District</Label>
              <input type="hidden" {...form.register("district")} />
              <Select
                value={String(form.watch("district") ?? "") || undefined}
                onValueChange={(value: string) => {
                  form.setValue("district", value);
                  form.setValue("city", "");
                }}
                disabled={!selectedStateIdStep2 || loadingDistrictsStep2}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedStateIdStep2
                        ? "Select state first"
                        : loadingDistrictsStep2
                          ? "Loading districts..."
                          : "Select district"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {districtsStep2.map(district => (
                    <SelectItem key={district.id} value={district.id}>
                      {district.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City</Label>
              <input type="hidden" {...form.register("city")} />
              <Select
                value={String(form.watch("city") ?? "") || undefined}
                onValueChange={(value: string) => {
                  form.setValue("city", value);
                }}
                disabled={!selectedDistrictIdStep2 || loadingCitiesStep2}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedDistrictIdStep2
                        ? "Select district first"
                        : loadingCitiesStep2
                          ? "Loading cities..."
                          : "Select city"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {citiesStep2.map(city => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pincode</Label>
              <Input
                {...form.register("pincode", {
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: "Enter a valid 6-digit pincode",
                  },
                })}
                placeholder="Enter pincode"
                maxLength={6}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {form.formState.errors.pincode && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.pincode.message)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 3) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">
            School Details (Only if its applicable)
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Board / University</Label>
              <Select
                onValueChange={(value: string) => form.setValue("board", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select board" />
                </SelectTrigger>
                <SelectContent>
                  {BOARDS.map(board => (
                    <SelectItem key={board.value} value={board.value}>
                      {board.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Medium of Instruction</Label>
              <div className="mt-1 grid grid-cols-2 gap-3 md:grid-cols-3">
                {MEDIUM_OF_INSTRUCTION.map(medium => {
                  const current = form.watch("medium") ?? [];
                  const selected = Array.isArray(current)
                    ? current.includes(medium.value)
                    : false;
                  return (
                    <label
                      key={medium.value}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors hover:bg-muted",
                        selected ? "border-primary bg-primary/10" : ""
                      )}
                    >
                      <Checkbox
                        id={`medium-${medium.value}`}
                        checked={selected}
                        onCheckedChange={checked => {
                          const isChecked = checked === true;
                          const next = isChecked
                            ? [...current, medium.value]
                            : current.filter((v: string) => v !== medium.value);
                          form.setValue("medium", next);
                        }}
                      />
                      <span className="text-sm font-medium">
                        {medium.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="pb-2 pt-4">
            <hr className="border-t" />
          </div>

          {/* <div>
            <Label>Accreditation</Label>
            <Select
              onValueChange={(value: string) =>
                form.setValue("accreditation", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select accreditation" />
              </SelectTrigger>
              <SelectContent>
                {ACCREDITATION.map(acc => (
                  <SelectItem key={acc.value} value={acc.value}>
                    {acc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div> */}
          <div>
            <Label>Education Levels</Label>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {INSTITUTION_LEVELS.map(level => (
                <label
                  key={level.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors hover:bg-muted",
                    selectedLevels.includes(level.value)
                      ? "border-primary bg-primary/10"
                      : ""
                  )}
                >
                  <Checkbox
                    id={`institution-level-${level.value}`}
                    checked={selectedLevels.includes(level.value)}
                    onCheckedChange={checked => {
                      const isChecked = checked === true;
                      const currentlySelected = selectedLevels.includes(
                        level.value
                      );
                      if (isChecked === currentlySelected) {
                        return;
                      }
                      toggleLevel(level.value);
                    }}
                  />
                  <span className="text-sm font-medium">{level.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pb-2 pt-4">
            <hr className="border-t" />
          </div>

          <div>
            <Label>Hostel Gender</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Select one or both (multiple values allowed). Sent as
              &quot;girls/boys&quot; when both selected.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {(() => {
                const raw = form.watch("boarding_gender");
                const currentArr =
                  typeof raw === "string" && raw
                    ? raw.split("/").filter(Boolean)
                    : Array.isArray(raw)
                      ? raw
                      : [];

                return BOARDING_GENDER.map(opt => {
                  const selected = currentArr.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors hover:bg-muted",
                        selected ? "border-primary bg-primary/10" : ""
                      )}
                    >
                      <Checkbox
                        id={`boarding-gender-${opt.value}`}
                        checked={selected}
                        onCheckedChange={checked => {
                          const isChecked = checked === true;
                          const next = isChecked
                            ? [...currentArr, opt.value]
                            : currentArr.filter(v => v !== opt.value);
                          form.setValue(
                            "boarding_gender",
                            next.length ? next.sort().join("/") : ""
                          );
                        }}
                      />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </label>
                  );
                });
              })()}
            </div>
          </div>

          {/* <div>
            <Label>Educational Year & Capacity</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Add one or more rows: educational year, total capacity, and
              vacant/occupied count.
            </p>
            <div className="mb-3 grid gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-5">
              <Input
                placeholder="e.g. 2025-2026"
                value={educationalYearDraft.educational_year}
                onChange={e =>
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    educational_year: e.target.value,
                  }))
                }
              />
              <Input
                type="number"
                min={0}
                placeholder="Total capacity"
                value={educationalYearDraft.total_capacity}
                onChange={e => {
                  const val = e.target.value;
                  const capacity = Number(val);
                  const occupied = Number(educationalYearDraft.occupied);
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    total_capacity: val,
                    vacant:
                      !isNaN(capacity) && !isNaN(occupied)
                        ? String(Math.max(0, capacity - occupied))
                        : prev.vacant,
                  }));
                }}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              <Input
                type="number"
                min={0}
                placeholder="Occupied"
                value={educationalYearDraft.occupied}
                onChange={e => {
                  const val = e.target.value;
                  const occupied = Number(val);
                  const capacity = Number(educationalYearDraft.total_capacity);
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    occupied: val,
                    vacant:
                      !isNaN(capacity) && !isNaN(occupied)
                        ? String(Math.max(0, capacity - occupied))
                        : prev.vacant,
                  }));
                }}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              <Input
                type="number"
                min={0}
                placeholder="Vacant"
                value={educationalYearDraft.vacant}
                onChange={e =>
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    vacant: e.target.value,
                  }))
                }
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                className="gap-1"
                onClick={() => {
                  const year = educationalYearDraft.educational_year.trim();
                  const capacity = Number(educationalYearDraft.total_capacity);
                  const vacant = Number(educationalYearDraft.vacant);
                  const occupied = Number(educationalYearDraft.occupied);
                  if (!year) {
                    return;
                  }
                  const current: {
                    educational_year: string;
                    total_capacity: number;
                    vacant: number;
                    occupied: number;
                  }[] = form.getValues("educational_year") ?? [];
                  form.setValue("educational_year", [
                    ...current,
                    {
                      _id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                      educational_year: year,
                      total_capacity: Number.isNaN(capacity) ? 0 : capacity,
                      vacant: Number.isNaN(vacant) ? 0 : vacant,
                      occupied: Number.isNaN(occupied) ? 0 : occupied,
                    },
                  ]);
                  setEducationalYearDraft({
                    educational_year: "",
                    total_capacity: "",
                    vacant: "",
                    occupied: "",
                  });
                }}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
            {(form.watch("educational_year") ?? []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Added entries
                </p>
                <div className="overflow-hidden rounded-lg border bg-background">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
                    <div>Educational Year</div>
                    <div>Total Capacity</div>
                    <div>Occupied</div>
                    <div>Vacant</div>
                    <div className="w-10" />
                  </div>
                  {(form.watch("educational_year") ?? []).map(
                    (
                      row: {
                        _id?: string;
                        educational_year: string;
                        total_capacity: number;
                        occupied: number;
                        vacant: number;
                      },
                      index: number
                    ) => (
                      <div
                        key={
                          (row as { _id?: string })._id ??
                          `${row.educational_year}-${row.total_capacity}-${row.vacant}`
                        }
                        className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
                      >
                        <div className="font-medium">
                          {row.educational_year}
                        </div>
                        <div>{row.total_capacity}</div>
                        <div>{row.occupied}</div>
                        <div>{row.vacant}</div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-9 w-10 text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            const current =
                              form.getValues("educational_year") ?? [];
                            form.setValue(
                              "educational_year",
                              current.filter(
                                (_: unknown, i: number) => i !== index
                              )
                            );
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div> */}

          <div className="pb-2 pt-4">
            <hr className="border-t" />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {/* <div>
              <Label>Affiliation Number</Label>
              <Input
                {...form.register("affiliation_number", {
                  pattern: {
                    value: /^[a-zA-Z0-9-]+$/,
                    message:
                      "Only alphanumeric characters and hyphens are allowed (e.g., INST-1234, ABC12345)",
                  },
                })}
                placeholder="Enter affiliation number"
              />
              {form.formState.errors.affiliation_number && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.affiliation_number.message)}
                </p>
              )}
            </div> */}
            <div>
              <Label>Registration Number</Label>
              <Input
                {...form.register("recognition_number")}
                placeholder="Enter recognition number"
              />
            </div>
          </div>

          <div className="pb-2 pt-4">
            <hr className="border-t" />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Admission Link</Label>
              <Input
                {...form.register("admission_link", {
                  validate: value =>
                    !value ||
                    /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i.test(
                      value
                    ) ||
                    "Enter a valid URL",
                })}
                placeholder="https://example.com/admission"
              />
              {form.formState.errors.admission_link && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.admission_link.message)}
                </p>
              )}
            </div>
            <div>
              <Label>Brochure Upload</Label>
              {existingBrochureUrl && (
                <div className="mb-2 flex items-center justify-between rounded-lg border bg-muted/30 p-2">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                      <Upload className="h-4 w-4 text-primary" />
                    </div>
                    <span className="truncate text-xs text-muted-foreground">
                      {existingBrochureUrl.split("/").pop()}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 text-destructive"
                    onClick={onRemoveExistingBrochure}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {brochureFile ? (
                <div className="mb-2 flex items-center justify-between rounded-lg border bg-muted/30 p-2">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                      <Upload className="h-4 w-4 text-primary" />
                    </div>
                    <span className="truncate text-xs text-muted-foreground">
                      {brochureFile.name}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 text-destructive"
                    onClick={() => setBrochureFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                !existingBrochureUrl && (
                  <div className="relative mt-1">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="absolute inset-0 z-10 cursor-pointer opacity-0"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setBrochureFile(file);
                        }
                      }}
                    />
                    <div className="flex h-10 items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground ring-offset-background hover:bg-muted/50">
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Brochure
                    </div>
                  </div>
                )
              )}
              <p className="mt-1 text-[10px] text-muted-foreground">
                Accepted: PDF, DOC, Images. Max 5MB.
              </p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 4) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Capacity & Facilities</h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              <h4 className="text-sm font-semibold">School Capacity</h4>
              <div>
                <Label>Total Students (In School)</Label>
                <Input
                  type="number"
                  {...form.register("total_students_in_school", { min: 0 })}
                  placeholder="Enter total students"
                  min={0}
                  className={NUMBER_INPUT_CLASSNAME}
                  onWheel={e => e.currentTarget.blur()}
                  onKeyDown={e => {
                    if (["e", "E", "-", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              <div>
                <Label>Total Classroom (In School)</Label>
                <Input
                  type="number"
                  {...form.register("total_classroom_in_school", { min: 0 })}
                  placeholder="Enter total classrooms"
                  min={0}
                  className={NUMBER_INPUT_CLASSNAME}
                  onWheel={e => e.currentTarget.blur()}
                  onKeyDown={e => {
                    if (["e", "E", "-", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              <h4 className="text-sm font-semibold">Hostel Capacity</h4>
              <div>
                <Label>Total Students (In Hostel)</Label>
                <Input
                  type="number"
                  {...form.register("total_students_in_hostel", { min: 0 })}
                  placeholder="Enter total students"
                  min={0}
                  className={NUMBER_INPUT_CLASSNAME}
                  onWheel={e => e.currentTarget.blur()}
                  onKeyDown={e => {
                    if (["e", "E", "-", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              <div>
                <Label>Total Rooms (In Hostel)</Label>
                <Input
                  type="number"
                  {...form.register("total_rooms_in_hostel", { min: 0 })}
                  placeholder="Enter total rooms"
                  min={0}
                  className={NUMBER_INPUT_CLASSNAME}
                  onWheel={e => e.currentTarget.blur()}
                  onKeyDown={e => {
                    if (["e", "E", "-", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="">
            <Label>Campus Area</Label>
            <p className="mb-3 text-xs text-muted-foreground">
              Enter campus area and choose unit (Sqft or Acre).
            </p>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <Input
                type="number"
                value={campusAreaValue}
                onChange={e => setCampusAreaValue(e.target.value)}
                placeholder={`Enter area in ${campusAreaUnit}`}
                min={0}
                className={cn(NUMBER_INPUT_CLASSNAME, "md:max-w-xs")}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />

              <Tabs
                value={campusAreaUnit}
                onValueChange={value =>
                  setCampusAreaUnit((value as "Sqft" | "Acre") || "Sqft")
                }
              >
                <TabsList className="h-11 rounded-lg border bg-background p-1">
                  <TabsTrigger
                    value="Sqft"
                    className="min-w-20 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg"
                  >
                    Sqft
                  </TabsTrigger>
                  <TabsTrigger
                    value="Acre"
                    className="min-w-20 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg"
                  >
                    Acre
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <input type="hidden" {...form.register("campus_area")} />
          </div>

          {/* <div>
            <Label>Departments / Faculties</Label>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {DEPARTMENTS.map(dept => (
                <label
                  key={dept}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors hover:bg-muted",
                    selectedDepartments.includes(dept) ? "border-primary bg-primary/10" : ""
                  )}
                >
                  <Checkbox
                    id={`department-${dept}`}
                    checked={selectedDepartments.includes(dept)}
                    onCheckedChange={checked => {
                      const isChecked = checked === true;
                      const currentlySelected = selectedDepartments.includes(dept);
                      if (isChecked === currentlySelected) return;
                      toggleDepartment(dept);
                    }}
                  />
                  <span className="text-sm font-medium">{dept}</span>
                </label>
              ))}
            </div>
          </div> */}
          <div className="pb-2 pt-4">
            <hr className="border-t" />
          </div>

          <div>
            <Label>Facilities Available</Label>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {loadingFacilities ? (
                <div className="col-span-full flex items-center gap-2 rounded-lg border px-3 py-4 text-sm text-muted-foreground">
                  <CustomLoader size="xs" />
                  Loading facilities...
                </div>
              ) : facilityOptions.length === 0 ? (
                <div className="col-span-full rounded-lg border px-3 py-4 text-sm text-muted-foreground">
                  No facilities available. Add records in Facilities Master.
                </div>
              ) : (
                facilityOptions.map(facility => (
                  <label
                    key={facility.id}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors hover:bg-muted",
                      selectedFacilities.includes(facility.id)
                        ? "border-primary bg-primary/10"
                        : ""
                    )}
                  >
                    <Checkbox
                      id={`facility-${facility.id}`}
                      checked={selectedFacilities.includes(facility.id)}
                      onCheckedChange={checked => {
                        const isChecked = checked === true;
                        const currentlySelected = selectedFacilities.includes(
                          facility.id
                        );
                        if (isChecked === currentlySelected) {
                          return;
                        }
                        toggleFacility(facility.id);
                      }}
                    />
                    <span className="text-sm font-medium">{facility.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 5) {
    return (
      <form
        onSubmit={form.handleSubmit(handleFinalSubmitWrapped)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">
            Trustee / Management Information
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Trustee / Principal Name</Label>
              <Input
                {...form.register("trustee_name")}
                placeholder="Enter name"
              />
            </div>
            <div>
              <Label>Designation</Label>
              <Select
                value={
                  String(form.watch("trustee_designation") ?? "") || undefined
                }
                onValueChange={(value: string) =>
                  form.setValue("trustee_designation", value)
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loadingDesignations
                        ? "Loading designations..."
                        : "Select designation"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {designations.map(d => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Trustee Phone</Label>
              <Input
                {...form.register("trustee_contact", {
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit phone number",
                  },
                })}
                placeholder="Enter phone number"
                maxLength={10}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {form.formState.errors.trustee_contact && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.trustee_contact.message)}
                </p>
              )}
            </div>
            <div>
              <Label>Trustee Email</Label>
              <Input
                type="email"
                {...form.register("trustee_email")}
                placeholder="Enter email"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Number of Trustees</Label>
              <Input
                type="number"
                {...form.register("trustee_count", { min: 0 })}
                placeholder="Enter count"
                min={0}
                className={NUMBER_INPUT_CLASSNAME}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div>
              <Label>Trust Registration Number</Label>
              <Input
                {...form.register("trust_registration")}
                placeholder="Enter registration number"
              />
            </div>
          </div>

          <div>
            <Label>Names of Other Trustees</Label>
            <Textarea
              {...form.register("other_trustees")}
              placeholder="Enter names separated by comma"
              rows={2}
            />
          </div>

          <div>
            <Label>Additional Comments</Label>
            <Textarea
              {...form.register("comments")}
              placeholder="Any additional information about your institution..."
              rows={3}
            />
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
            disabled={isFinalSubmitting}
          >
            Back
          </Button>
          <Button
            type="submit"
            className="flex-1"
            size="lg"
            disabled={isFinalSubmitting}
          >
            {isFinalSubmitting ? (
              <>
                {/* <CustomLoader size="md" /> */}
                {isEditMode ? "Updating…" : "Submitting…"}
              </>
            ) : isEditMode ? (
              "Update Institution"
            ) : (
              "Submit Registration"
            )}
          </Button>
        </div>
      </form>
    );
  }

  return null;
}
